import { fabric } from "fabric";
import {
  EAlignFacesOptions,
  EFitOptions,
  TransformOptions,
} from "filestack-js";

import roundNumber from "../../rounded-number";
import { BorderedGroup, BorderedImage } from "../elements/bordered-objects";
import { FileItem } from "../state/types";
import type { RenderContext } from "./fabric-renderer";
import { IObjectOptions } from "fabric/fabric-impl";

interface ImageFile {
  handle: string;
  file_type: string;
}

export function renderFile(
  renderContext: RenderContext,
  item: FileItem,
  element: fabric.Group,
  canvas: fabric.Canvas,
): fabric.Group {
  const options: IObjectOptions = {
    left: item.left,
    top: item.top,
    width: item.width,
    height: item.height,
    angle: item.angle,
  };

  if (!element) {
    element = new BorderedGroup([], options);
  }

  // Filter images from the field value
  const fieldValue = renderContext.record?.[item.field_slug];
  const fieldImages = filterFieldImages(fieldValue);

  if (!fieldImages.length) {
    // Remove all objects from the group if there are no images
    element.forEachObject((obj) => element.remove(obj));
  } else {
    const displayMode = item.displayMode ?? "first-image";

    if (displayMode === "first-image") {
      renderFirstImage(renderContext, item, element, canvas, fieldImages[0]);
    } else {
      renderAllImages(renderContext, item, element, canvas, fieldImages).catch(
        console.error,
      );
    }
  }

  element.setOptions({
    id: item.id,
    ...options,
    lockScalingFlip: true,
    clipPath: makeGroupClipPath(item.width as number, item.height as number),
  });

  return element;
}

export function makeGroupClipPath(width: number, height: number): fabric.Rect {
  return new fabric.Rect({
    originX: "center",
    originY: "center",
    width,
    height,
  });
}

function filterFieldImages(value: unknown): ImageFile[] {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return [];
  }

  const files = value as ImageFile[];
  return files.filter((file) => file.file_type === "image");
}

function renderFirstImage(
  renderContext: RenderContext,
  item: FileItem,
  groupElement: fabric.Group,
  canvas: fabric.Canvas,
  { handle }: ImageFile,
): void {
  // Create an image element if it doesn't exist
  let imageEl = groupElement.getObjects()[0] as BorderedImage;
  if (!imageEl) {
    imageEl = new BorderedImage(new Image(), { selectable: false });
    groupElement.add(imageEl);
  }

  // Remove other objects from the group
  groupElement.forEachObject((obj) => {
    if (obj !== imageEl) {
      groupElement.remove(obj);
    }
  });

  const fitMode = item.fitMode ?? "fit";
  const resizeFitMode =
    fitMode === "fit"
      ? EFitOptions.clip
      : fitMode === "fill"
        ? EFitOptions.crop
        : EFitOptions.scale;

  const url = renderContext.filestack?.transform(handle, {
    resize: {
      height: item.height,
      width: item.width,
      fit: resizeFitMode,
      align: EAlignFacesOptions.center,
    },
  });

  if (url && imageEl.getElement().src !== url) {
    fabric.Image.fromURL(
      url,
      (img) => {
        if (
          img.getElement().className === "canvas-img" &&
          item.width &&
          img.width &&
          item.height &&
          img.height
        ) {
          const scale = Math.min(
            item.width / img.width,
            item.height / img.height,
          );
          const htmlImg = img.getElement() as HTMLImageElement;
          let left = 0;

          const shouldCenter = fitMode === "fit";
          if (shouldCenter) {
            left = item.width / 2 - (img.width * scale) / 2;
          }

          imageEl.setElement(htmlImg, {
            scaleX: roundNumber(scale, 2),
            scaleY: roundNumber(scale, 2),
            width: item.width,
            height: item.height,
            left,
          });
        } else {
          imageEl.setElement(new Image(150, 150), {
            width: item.width,
            height: item.height,
          });
        }

        groupElement.dirty = true;
        canvas.requestRenderAll();
        imageEl.setCoords();
      },
      { crossOrigin: "anonymous" },
    );
  }

  imageEl.setOptions({
    originX: "center",
    originY: "center",
    opacity: 1,
  });
}

async function renderAllImages(
  renderContext: RenderContext,
  item: FileItem,
  groupElement: fabric.Group,
  canvas: fabric.Canvas,
  imageFiles: ImageFile[],
): Promise<void> {
  const imageElements = groupElement.getObjects() as BorderedImage[];
  const {
    width: containerWidth = 0,
    height: containerHeight = 0,
    imagePadding = 0,
  } = item;
  const isVisible = containerWidth > 0 && containerHeight > 0;

  // Render all images and get the HTMLImageElement instances
  const htmlImages = isVisible
    ? await renderHtmlImages(renderContext, item, imageFiles)
    : [];

  // Render images sequentially. On line overflow (bigger than the container width),
  // the next image will be rendered on the next line.
  let top = 0;
  let left = 0;
  let renderedImages = 0;
  let currentLineStartIdx = 0;

  for (const htmlImg of htmlImages) {
    // Get or create an image element
    let imageEl = imageElements[renderedImages++];
    if (!imageEl) {
      imageEl = new BorderedImage(htmlImg, { selectable: false });
      groupElement.add(imageEl);
    }

    // Calculate element position
    const doesOverflowContainer =
      renderedImages > 1 && left + htmlImg.width > containerWidth;

    if (doesOverflowContainer) {
      left = 0;

      // Calculate highest height in the current line
      const highestHeight = htmlImages
        .slice(currentLineStartIdx, renderedImages)
        .reduce((maxHeight, img) => {
          return Math.max(maxHeight, img.height);
        }, 0);

      top += highestHeight + imagePadding;
      currentLineStartIdx = renderedImages;
    }

    // Set image element properties
    imageEl.setElement(htmlImg, {
      width: containerWidth,
      height: containerHeight,
      left,
      top,
    });

    left += htmlImg.width + imagePadding;

    imageEl.setOptions({
      originX: "center",
      originY: "center",
      opacity: 1,
    });
  }

  // Remove extra image elements
  for (let i = renderedImages; i < imageElements.length; i++) {
    groupElement.remove(imageElements[i]);
  }

  groupElement.dirty = true;
  canvas.requestRenderAll();
  groupElement.setCoords();
}

function getAllImagesImageTransformOptions(item: FileItem): TransformOptions {
  const imageSize = item.imageSize as number;
  const { sizingMode = "square" } = item;
  const resizeOptions: TransformOptions["resize"] = {
    fit: EFitOptions.crop,
    align: EAlignFacesOptions.center,
  };

  const shouldSetWidth =
    sizingMode === "square" || sizingMode === "fixed-width";
  if (shouldSetWidth) {
    resizeOptions.width = imageSize;
  }

  const shouldSetHeight =
    sizingMode === "square" || sizingMode === "fixed-height";
  if (shouldSetHeight) {
    resizeOptions.height = imageSize;
  }

  return { resize: resizeOptions };
}

async function renderHtmlImages(
  renderContext: RenderContext,
  item: FileItem,
  imageFiles: ImageFile[],
): Promise<HTMLImageElement[]> {
  const transformOptions = getAllImagesImageTransformOptions(item);
  const promises = imageFiles.map(({ handle }) => {
    return new Promise((resolve) => {
      const url = renderContext.filestack?.transform(handle, transformOptions);
      if (!url) {
        return resolve(null);
      }

      fabric.Image.fromURL(
        url,
        (img) => {
          const htmlImg = img.getElement() as HTMLImageElement;
          if (
            img.getElement().className === "canvas-img" &&
            img.width &&
            img.height
          ) {
            resolve(htmlImg);
          } else {
            resolve(null);
          }
        },
        { crossOrigin: "anonymous" },
      );
    });
  });

  const results = await Promise.all(promises);
  return results.filter(Boolean) as HTMLImageElement[];
}
